import React from 'react';
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
} from '@material-ui/core';
import apiURL from '../apiURL';

const formatDate = value => {
    if (!value) return '—';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '—';
    }

    return date.toLocaleString('fr-FR');
};

const TNR_STATUS_LABELS = {
    CERTIFIED_OK: 'CERTIFIÉ OK',
    OK: 'OK',
    KNOWN_ISSUE: 'ANOMALIE CONNUE',
    NEW_REGRESSION: 'NOUVELLE RÉGRESSION',
    FAILED: 'ÉCHEC',
    NOT_CERTIFIED: 'NON CERTIFIÉ',
    INFO: 'INFORMATION',
    QUEUED: 'EN ATTENTE',
    ALREADY_ACTIVE: 'CONTRÔLE DÉJÀ EN COURS',
    SUCCESS: 'SUCCÈS',
};

const tnrStatusLabel = value =>
    value
        ? TNR_STATUS_LABELS[value] || 'STATUT INCONNU'
        : '—';

const TNR_MODE_LABELS = {
    READ_ONLY: 'LECTURE SEULE',
};

const tnrModeLabel = value =>
    value
        ? TNR_MODE_LABELS[value] || 'MODE INCONNU'
        : '—';


const severityLabel = level => ({
    5: 'CRITIQUE',
    4: 'ÉLEVÉE',
    3: 'MOYENNE',
    2: 'FAIBLE',
    1: 'INFORMATION',
}[level] || 'MOYENNE');

const getAnomalyPresentation = check => {
    const ref = String(check.known_issue_id || '').toUpperCase();
    const name = String(check.name || '').toLowerCase();

    const make = (
        severity,
        title,
        impact,
        action,
        zone,
        validation
    ) => ({
        severity,
        severityLabel: severityLabel(severity),
        title,
        impact,
        action,
        zone,
        validation,
    });

    if (ref === 'SMS-EVENT-001' || name.includes('sms')) {
        return make(
            4,
            "Envoi du lien de l'événement par SMS en panne",
            "Le bouton d'envoi par SMS ne permet pas au client d'envoyer correctement le lien de son événement.",
            "Rétablir l'envoi du SMS depuis la page événement.",
            "Fonction d'envoi SMS des événements",
            "Le bouton envoie réellement le SMS sans message d'erreur."
        );
    }

    if (ref === 'API-BOX-001') {
        return make(
            4,
            "Enregistrement des paramètres de contrat Box défaillant",
            "Certaines modifications du contrat Box peuvent provoquer une erreur serveur au lieu d'être enregistrées.",
            "Sécuriser l'enregistrement lorsque le fichier de signature est absent.",
            "API Box — paramètres du contrat",
            "La modification des paramètres fonctionne avec ou sans nouvelle signature."
        );
    }

    if (
        ref === 'MON-DATA-001' ||
        (name.includes('data') && name.includes('missing'))
    ) {
        return make(
            4,
            "Fichiers ou images manquants provoquant une erreur serveur",
            "Certaines images ou ressources absentes peuvent renvoyer une erreur 500 au lieu d'une réponse propre. Cela peut empêcher leur affichage sur le site.",
            "Faire retourner une réponse 404 propre lorsqu'un fichier n'existe pas.",
            "Serveur des images et fichiers data.karaoke-kreyol.com",
            "Un fichier inexistant renvoie 404 et ne provoque plus d'erreur 500."
        );
    }

    if (ref === 'SEC-TRAEFIK-001' || name.includes('traefik_public')) {
        return make(
            4,
            "Interface technique Traefik accessible depuis Internet",
            "Cela ne bloque pas directement les clients, mais expose une partie technique du serveur qui devrait rester privée.",
            "Fermer l'accès public à l'interface technique Traefik.",
            "Traefik / accès réseau",
            "L'interface Traefik n'est plus accessible publiquement."
        );
    }

    if (ref === 'SEC-NET-001' || name.includes('direct_host')) {
        return make(
            4,
            "Ports techniques du serveur accessibles directement",
            "Des services internes peuvent être atteints directement depuis Internet au lieu de passer uniquement par les accès prévus.",
            "Fermer les ports techniques publics qui ne sont pas nécessaires.",
            "Réseau / ports Docker du serveur",
            "Les ports techniques concernés ne répondent plus depuis Internet."
        );
    }

    if (ref === 'SEC-WHOAMI-001' || name.includes('whoami')) {
        return make(
            3,
            "Service de test Whoami encore accessible publiquement",
            "Ce service ne gêne pas les clients, mais il s'agit d'un outil technique de test qui n'a pas besoin d'être exposé sur Internet.",
            "Supprimer ou fermer son accès public.",
            "Service Whoami / Traefik",
            "Le service Whoami n'est plus accessible publiquement."
        );
    }

    if (ref === 'INFRA-TRAEFIK-001') {
        return make(
            3,
            "Erreurs de détection de services dans Traefik",
            "Traefik détecte certains conteneurs de manière incorrecte. Cela encombre les logs et peut compliquer le routage de futurs services.",
            "Exclure ou configurer correctement les conteneurs concernés.",
            "Configuration Docker / Traefik",
            "Les erreurs Traefik concernées disparaissent des nouveaux logs."
        );
    }

    if (ref === 'ROUTE-WWW-001' || name.includes('www_redirect')) {
        return make(
            2,
            "Redirection de l'adresse www incorrecte",
            "Le site reste accessible, mais l'adresse avec www effectue une redirection inutile ou incorrecte avant d'arriver sur le site principal.",
            "Corriger la redirection www pour aller directement vers l'adresse HTTPS principale.",
            "Redirection www.karaoke-kreyol.com",
            "www.karaoke-kreyol.com redirige directement et proprement vers le site HTTPS."
        );
    }

    if (ref === 'SEC-CRED-001') {
        return make(
            5,
            "Identifiant technique sensible à remplacer",
            "Un identifiant technique a pu être exposé. Il doit être remplacé afin d'éviter qu'il puisse être réutilisé.",
            "Révoquer l'ancien identifiant et créer un nouvel identifiant sécurisé.",
            "Identifiants d'infrastructure",
            "L'ancien identifiant est inutilisable et le nouveau est installé."
        );
    }

    return make(
        check.status === 'NEW_REGRESSION' ? 5 : 3,
        "Anomalie détectée par le contrôle automatique",
        check.detail || "Le contrôle a détecté un comportement différent de celui attendu.",
        "Corriger uniquement le contrôle ou la fonctionnalité indiquée par cette anomalie.",
        check.name || "Zone indiquée par le contrôle TNR",
        "Le même contrôle doit repasser en OK après correction."
    );
};


const getInfoPresentation = check => {
    const name = String(check.name || '').toLowerCase();

    if (name.includes('api_box_signature')) {
        return {
            title: 'Activité des signatures Box',
            text: "Aucune erreur de signature Box n'a été détectée dans les logs des dernières 24 h.",
        };
    }

    if (name.includes('monitor_dns')) {
        return {
            title: 'Domaine technique de surveillance',
            text: "Ce domaine technique n'est pas utilisé pour vérifier la disponibilité du site client. Cela ne bloque pas le karaoké.",
        };
    }

    if (name.includes('traefik') && name.includes('port')) {
        return {
            title: 'Surveillance Traefik',
            text: "Aucune nouvelle erreur de port Traefik n'a été détectée pendant la période contrôlée.",
        };
    }

    return {
        title: 'Information du contrôle automatique',
        text: check.detail ||
            "Le TNR signale une information de surveillance. Il ne s'agit pas d'une panne.",
    };
};


const TNR_CHECK_LABELS = {
    docker_nginx: 'Serveur web Nginx',
    docker_front: 'Interface publique du site',
    docker_api: 'API du site',
    docker_traefik: 'Routage HTTPS Traefik',
    docker_whoami: 'Service technique Whoami',
    front_home: "Page d'accueil du site",
    admin_home: "Page d'accueil de l'administration",
    api_swagger: "Documentation technique de l'API",
    data_favicon: 'Icône du site',
    data_missing_file: 'Gestion des fichiers ou images manquants',
    www_redirect: "Redirection de l'adresse www",
    whoami_public: 'Accès public au service Whoami',
    traefik_public: "Accès public à l'interface Traefik",
    'tls_karaoke-kreyol.com': 'Certificat HTTPS du site',
    'tls_admin.karaoke-kreyol.com': "Certificat HTTPS de l'administration",
    'tls_api.karaoke-kreyol.com': "Certificat HTTPS de l'API",
    'tls_data.karaoke-kreyol.com': 'Certificat HTTPS du serveur de fichiers',
    direct_host_ports: 'Ports techniques accessibles depuis Internet',
    nginx_data_cycles_24h: 'Boucles serveur sur les fichiers/images sur 24 h',
    nginx_other_errors_24h: 'Autres erreurs Nginx sur 24 h',
    api_box_signature_24h: 'Erreurs de signature Box sur 24 h',
    api_other_strong_errors_24h: "Erreurs importantes de l'API sur 24 h",
    traefik_port_missing_24h: 'Erreurs de port Traefik sur 24 h',
    traefik_other_errors_24h: 'Autres erreurs Traefik sur 24 h',
    monitor_dns: 'Domaine technique de surveillance',
};

const tnrCheckLabel = name =>
    TNR_CHECK_LABELS[name] || 'Contrôle technique';

const TnrDashboard = () => {
    const [summary, setSummary] = React.useState(null);
    const [runs, setRuns] = React.useState([]);
    const [selectedRun, setSelectedRun] = React.useState(null);
    const [expandedRunId, setExpandedRunId] = React.useState(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState(null);
    const [copiedTicketKey, setCopiedTicketKey] = React.useState(null);
    const [runLoading, setRunLoading] = React.useState(false);
    const [runMessage, setRunMessage] = React.useState(null);
    const [runError, setRunError] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const loadSummary = React.useCallback(async () => {
        setLoading(true);
        setError(null);

        try {

            const headers = {
                Accept: 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('auth') || ''}`,
                'Cache-Control':
                    'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
            };

            const [summaryResponse, runsResponse] =
                await Promise.all([
                    fetch(
                        `/tnr-proxy/summary`,
                        {
                            method: 'GET',
                            cache: 'no-store',
                            headers,
                        }
                    ),
                    fetch(
                        `/tnr-proxy/runs?limit=20`,
                        {
                            method: 'GET',
                            cache: 'no-store',
                            headers,
                        }
                    ),
                ]);

            if (!summaryResponse.ok) {
                throw new Error(
                    `SUMMARY HTTP ${summaryResponse.status}`
                );
            }

            if (!runsResponse.ok) {
                throw new Error(
                    `RUNS HTTP ${runsResponse.status}`
                );
            }

            setSummary(await summaryResponse.json());
            setRuns(await runsResponse.json());
        } catch (err) {
            console.error(err);
            setError(
                'Impossible de charger les données TNR.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const loadRunDetail = React.useCallback(async id => {
        if (!id) return;

        setDetailLoading(true);
        setDetailError(null);

        try {

            const response = await fetch(
                `/tnr-proxy/runs/${id}`,
                {
                    method: 'GET',
                    cache: 'no-store',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('auth') || ''}`,
                                'Cache-Control':
                            'no-cache, no-store, must-revalidate',
                        Pragma: 'no-cache',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `RUN DETAIL HTTP ${response.status}`
                );
            }

            setSelectedRun(await response.json());
        } catch (err) {
            console.error(err);
            setDetailError(
                'Impossible de charger le détail de cette campagne.'
            );
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const toggleRunDetail = React.useCallback(id => {
        if (expandedRunId === id) {
            setExpandedRunId(null);
            setSelectedRun(null);
            setDetailError(null);
            return;
        }

        setExpandedRunId(id);
        setSelectedRun(null);
        setDetailError(null);
        loadRunDetail(id);
    }, [expandedRunId, loadRunDetail]);

    const launchTnrRun = React.useCallback(async () => {
        setRunLoading(true);
        setRunMessage(null);
        setRunError(null);

        try {

            const response = await fetch(
                `/tnr-proxy/run`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('auth') || ''}`,
                            },
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`RUN HTTP ${response.status}`);
            }

            if (data.status === 'QUEUED') {
                setRunMessage('Contrôle TNR lancé.');
            } else if (data.status === 'ALREADY_ACTIVE') {
                setRunMessage('Un contrôle TNR est déjà en cours.');
            } else {
                setRunMessage(`État : ${tnrStatusLabel(data.status)}`);
            }
        } catch (err) {
            console.error(err);
            setRunError('Impossible de lancer le contrôle TNR.');
        } finally {
            setRunLoading(false);
        }
    }, []);

    const latestRun =
        summary && summary.latest_run
            ? summary.latest_run
            : null;

    const checks =
        summary && Array.isArray(summary.checks)
            ? summary.checks
            : [];

    const countStatus = status =>
        checks.filter(check =>
            check && check.last_status === status
        ).length;

    const okCount = countStatus('OK');
    const knownIssueCount = countStatus('KNOWN_ISSUE');
    const regressionCount = countStatus('NEW_REGRESSION');
    const infoCount = countStatus('INFO');

    const selectedRunChecks =
        selectedRun && Array.isArray(selectedRun.checks)
            ? selectedRun.checks
            : [];

    const selectedRunAnomalies =
        selectedRunChecks
            .filter(check =>
                check &&
                (
                    check.status === 'KNOWN_ISSUE' ||
                    check.status === 'NEW_REGRESSION'
                )
            )
            .sort(
                (a, b) =>
                    getAnomalyPresentation(b).severity -
                    getAnomalyPresentation(a).severity
            );

    const selectedRunInformations =
        selectedRunChecks.filter(
            check => check && check.status === 'INFO'
        );

    const overall =
        latestRun && latestRun.overall
            ? latestRun.overall
            : 'NON_CERTIFIE';

    const copyRepairTicket = async check => {
        if (!selectedRun || !check) return;

        const evidence =
            check.evidence
                ? (
                    typeof check.evidence === 'string'
                        ? check.evidence
                        : JSON.stringify(check.evidence, null, 2)
                )
                : 'À compléter';

        const presentation =
            getAnomalyPresentation(check);

        const priority =
            selectedRunAnomalies.findIndex(
                item => item === check
            ) + 1;

        const usefulEvidence =
            String(evidence || '')
                .replace(/\\s+/g, ' ')
                .slice(0, 600);

        const ticket = [
            'TNR — TICKET DE RÉPARATION',
            '',
            `PRIORITÉ : ${priority}/${selectedRunAnomalies.length}`,
            `GRAVITÉ : ${presentation.severity}/5 — ${presentation.severityLabel}`,
            `PANNE : ${presentation.title}`,
            '',
            `IMPACT CONCRET : ${presentation.impact}`,
            '',
            `CORRECTION ATTENDUE : ${presentation.action}`,
            `ZONE CIBLÉE : ${presentation.zone}`,
            `RÉFÉRENCE : ${check.known_issue_id || check.name || 'TNR'}`,
            '',
            `PREUVE UTILE : ${check.detail || usefulEvidence || 'Voir le contrôle TNR'}`,
            '',
            `VALIDATION APRÈS CORRECTION : ${presentation.validation}`,
            '',
            'APRÈS LA CORRECTION : lancer un contrôle TNR, actualiser la page et vérifier que cette anomalie a disparu.',
            '',
            'CONSIGNE : corriger uniquement cette anomalie.',
            'Ne pas refaire un audit général sauf si la correction ciblée échoue.',
        ].join('\n');

        try {
            if (
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                await navigator.clipboard.writeText(ticket);
            } else {
                const textarea =
                    document.createElement('textarea');

                textarea.value = ticket;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';

                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            const key =
                `${check.name || ''}:${check.status || ''}`;

            setCopiedTicketKey(key);

            window.setTimeout(() => {
                setCopiedTicketKey(current =>
                    current === key ? null : current
                );
            }, 2000);
        } catch (err) {
            console.error(err);
            setDetailError(
                'Impossible de copier le ticket de réparation.'
            );
        }
    };

    return (
        <div style={{padding: 24}}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                }}
            >
                <div>
                    <Typography variant="h4">
                        TNR
                    </Typography>
                    <Typography color="textSecondary">
                        Santé et non-régression de la production
                    </Typography>
                </div>

                <div style={{display: 'flex', gap: 10}}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={launchTnrRun}
                        disabled={runLoading}
                    >
                        {runLoading
                            ? 'Lancement...'
                            : 'LANCER UN CONTRÔLE TNR'}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={loadSummary}
                        disabled={loading}
                    >
                        {loading ? 'Actualisation...' : 'Actualiser'}
                    </Button>
                </div>
            </div>

            {runMessage && (
                <Card style={{marginBottom: 20}}>
                    <CardContent>
                        <Typography color="primary">
                            {runMessage}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {runError && (
                <Card style={{marginBottom: 20}}>
                    <CardContent>
                        <Typography color="error">
                            {runError}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {loading && !summary && (
                <div style={{padding: 40, textAlign: 'center'}}>
                    <CircularProgress />
                </div>
            )}

            {error && (
                <Card style={{marginBottom: 20}}>
                    <CardContent>
                        <Typography color="error">
                            {error}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {summary && (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary">
                                        État général
                                    </Typography>
                                    <Typography variant="h5">
                                        {tnrStatusLabel(overall)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary">
                                        Contrôles
                                    </Typography>
                                    <Typography variant="h5">
                                        {summary.check_count || checks.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary">
                                        OK
                                    </Typography>
                                    <Typography variant="h5">
                                        {okCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary">
                                        Anomalies connues
                                    </Typography>
                                    <Typography variant="h5">
                                        {knownIssueCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3} md={2}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary">
                                        Régressions
                                    </Typography>
                                    <Typography variant="h5">
                                        {regressionCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card style={{marginTop: 20}}>
                        <CardContent>
                            <Typography variant="h6">
                                Dernière campagne
                            </Typography>
                            <Typography>
                                Date : {formatDate(
                                    latestRun && latestRun.generated_at
                                )}
                            </Typography>
                            <Typography>
                                Mode : {
                                    latestRun && latestRun.mode ? tnrModeLabel(latestRun.mode) : '—'
                                }
                            </Typography>
                            <Typography>
                                Informations : {infoCount}
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card style={{marginTop: 20}}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                style={{marginBottom: 12}}
                            >
                                Historique des contrôles TNR
                            </Typography>

                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{marginBottom: 12}}
                            >
                                Chaque ligne correspond à un contrôle complet du site, pas à une anomalie.
                            </Typography>

                            {runs.length === 0 && (
                                <Typography color="textSecondary">
                                    Aucun historique disponible.
                                </Typography>
                            )}

                            {runs.map(run => {
                                const isOpen = expandedRunId === run._id;

                                const latestChecks =
                                    runs.length > 0 && Array.isArray(runs[0].checks)
                                        ? runs[0].checks
                                        : [];

                                const activeKeys = latestChecks
                                    .filter(check =>
                                        check.status === 'KNOWN_ISSUE' ||
                                        check.status === 'NEW_REGRESSION'
                                    )
                                    .map(check =>
                                        check.known_issue_id || check.name
                                    );

                                const runProblemKeys = Array.isArray(run.checks)
                                    ? run.checks
                                        .filter(check =>
                                            check.status === 'KNOWN_ISSUE' ||
                                            check.status === 'NEW_REGRESSION'
                                        )
                                        .map(check =>
                                            check.known_issue_id || check.name
                                        )
                                    : [];

                                const activeProblemCount = runProblemKeys.filter(
                                    key => activeKeys.indexOf(key) !== -1
                                ).length;

                                const correctedProblemCount =
                                    runProblemKeys.length - activeProblemCount;

                                const regressionCount = Array.isArray(run.checks)
                                    ? run.checks.filter(check =>
                                        check.status === 'NEW_REGRESSION'
                                    ).length
                                    : 0;

                                return (
                                    <React.Fragment
                                    >
                                <div
                                    key={run._id || run.report_sha256}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleRunDetail(run._id)}
                                    onKeyDown={event => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            toggleRunDetail(run._id);
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 16,
                                        padding: '12px 0',
                                        borderBottom: '1px solid #ddd',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div>
                                        <Typography>
                                            {formatDate(run.generated_at)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {Array.isArray(run.checks)
                                                ? run.checks.filter(check =>
                                                    check.status === 'OK' ||
                                                    check.status === 'CERTIFIED_OK'
                                                ).length
                                                : 0} OK
                                            {' • '}
                                            {Array.isArray(run.checks)
                                                ? run.checks.filter(check =>
                                                    check.status === 'KNOWN_ISSUE'
                                                ).length
                                                : 0} anomalies
                                            {regressionCount > 0 && (
                                                <React.Fragment>
                                                    {' • '}
                                                    {regressionCount} {
                                                        regressionCount === 1
                                                            ? 'régression'
                                                            : 'régressions'
                                                    }
                                                </React.Fragment>
                                            )}
                                            {' • '}
                                            {Array.isArray(run.checks)
                                                ? run.checks.filter(check =>
                                                    check.status === 'INFO'
                                                ).length
                                                : 0} informations
                                        </Typography>
                                    </div>

                                    <div style={{textAlign: 'right'}}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: 6,
                                                marginBottom: 6,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {activeProblemCount > 0 && (
                                                <span
                                                    style={{
                                                        padding: '3px 9px',
                                                        borderRadius: 999,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        backgroundColor: '#ed6c02',
                                                    }}
                                                >
                                                    {run._id === runs[0]._id
                                                        ? `${activeProblemCount} À TRAITER`
                                                        : `${activeProblemCount} ENCORE ${
                                                            activeProblemCount === 1
                                                                ? 'ACTIVE'
                                                                : 'ACTIVES'
                                                        }`}
                                                </span>
                                            )}

                                            {correctedProblemCount > 0 && (
                                                <span
                                                    style={{
                                                        padding: '3px 9px',
                                                        borderRadius: 999,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        backgroundColor: '#2e7d32',
                                                    }}
                                                >
                                                    {activeProblemCount === 0
                                                        ? 'TOUT CORRIGÉ'
                                                        : `${correctedProblemCount} ${
                                                            correctedProblemCount === 1
                                                                ? 'CORRIGÉE'
                                                                : 'CORRIGÉES'
                                                        }`}
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <strong>
                                                {isOpen
                                                    ? 'Masquer le détail ⌃'
                                                    : 'Voir le détail ›'}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div
                                        style={{
                                            padding: '18px 16px 24px',
                                            background: '#fafafa',
                                            borderBottom: '1px solid #ddd',
                                        }}
                                    >

                            <Typography
                                variant="h6"
                                style={{marginBottom: 12}}
                            >
                                Détail du contrôle
                            </Typography>

                            {!selectedRun && !detailLoading && (
                                <Typography color="textSecondary">
                                    Chargement du détail…
                                </Typography>
                            )}

                            {detailLoading && (
                                <CircularProgress size={24} />
                            )}

                            {detailError && (
                                <Typography color="error">
                                    {detailError}
                                </Typography>
                            )}

                            {selectedRun && !detailLoading && (
                                <>
                                    <Typography>
                                        Date : {formatDate(
                                            selectedRun.generated_at
                                        )}
                                    </Typography>

                                    <Typography>
                                        État : {tnrStatusLabel(selectedRun.overall)}
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        style={{marginTop: 16}}
                                    >
                                        Anomalies ({selectedRunAnomalies.length})
                                    </Typography>

                                    {selectedRunAnomalies.length === 0 && (
                                        <Typography color="textSecondary">
                                            Aucune anomalie dans cette campagne.
                                        </Typography>
                                    )}

                                    {selectedRunAnomalies.map((check, index) => {
                                            const presentation =
                                                getAnomalyPresentation(check);

                                            const problemKey =
                                                check.known_issue_id || check.name;

                                            const currentCheck =
                                                latestChecks.find(
                                                    current =>
                                                        (
                                                            current.status === 'KNOWN_ISSUE' ||
                                                            current.status === 'NEW_REGRESSION'
                                                        ) &&
                                                        (
                                                            current.known_issue_id ||
                                                            current.name
                                                        ) === problemKey
                                                );

                                            const problemBadge = currentCheck
                                                ? currentCheck.status === 'NEW_REGRESSION'
                                                    ? {
                                                        label: 'RÉGRESSION ACTUELLE',
                                                        backgroundColor: '#d32f2f',
                                                    }
                                                    : {
                                                        label: 'ENCORE À CORRIGER',
                                                        backgroundColor: '#ed6c02',
                                                    }
                                                : {
                                                    label: 'CORRIGÉE',
                                                    backgroundColor: '#2e7d32',
                                                };


                                            return (
                                                <div
                                                    key={
                                                        check.name +
                                                        ':' +
                                                        check.status
                                                    }
                                                    style={{
                                                        marginTop: 14,
                                                        padding: 16,
                                                        border:
                                                            '1px solid #ddd',
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                fontSize: 11,
                                                                fontWeight: 700,
                                                                color: '#fff',
                                                                backgroundColor:
                                                                    problemBadge.backgroundColor,
                                                            }}
                                                        >
                                                            {problemBadge.label}
                                                        </span>
                                                    </div>
                                                    <Typography
                                                        variant="subtitle1"
                                                        style={{
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        PRIORITÉ {index + 1} SUR {
                                                            selectedRunAnomalies.length
                                                        }
                                                        {' — '}
                                                        GRAVITÉ {
                                                            presentation.severity
                                                        }/5 — {
                                                            presentation.severityLabel
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="h6"
                                                        style={{
                                                            marginTop: 8,
                                                        }}
                                                    >
                                                        {presentation.title}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginTop: 12,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Ce que ça provoque
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        {presentation.impact}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginTop: 12,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        À faire
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        {presentation.action}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginTop: 12,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Après correction
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        {presentation.validation}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginTop: 12,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        Une fois corrigé
                                                    </Typography>

                                                    <Typography variant="body2">
                                                        Lancer un contrôle TNR → Actualiser → vérifier que cette anomalie a disparu.
                                                    </Typography>

                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() =>
                                                            copyRepairTicket(check)
                                                        }
                                                        style={{marginTop: 14}}
                                                    >
                                                        {
                                                            copiedTicketKey ===
                                                            `${
                                                                check.name || ''
                                                            }:${
                                                                check.status || ''
                                                            }`
                                                                ? 'TICKET COPIÉ'
                                                                : 'COPIER LE TICKET DE RÉPARATION'
                                                        }
                                                    </Button>

                                                    <details
                                                        style={{marginTop: 14}}
                                                    >
                                                        <summary
                                                            style={{
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            Voir les détails techniques
                                                        </summary>

                                                        <Typography
                                                            variant="body2"
                                                            style={{marginTop: 8}}
                                                        >
                                                            Référence : {
                                                                check.known_issue_id ||
                                                                'Non renseignée'
                                                            }
                                                        </Typography>

                                                        <Typography variant="body2">
                                                            Contrôle : {
                                                                check.name ||
                                                                'Non renseigné'
                                                            }
                                                        </Typography>

                                                        <Typography variant="body2">
                                                            État : {
                                                                tnrStatusLabel(
                                                                    check.status
                                                                )
                                                            }
                                                        </Typography>

                                                        <Typography variant="body2">
                                                            Zone ciblée : {
                                                                presentation.zone
                                                            }
                                                        </Typography>

                                                        {check.detail && (
                                                            <Typography variant="body2">
                                                                Détail : {
                                                                    check.detail
                                                                }
                                                            </Typography>
                                                        )}
                                                    </details>
                                                </div>
                                            );
                                        })}

                                        <Typography
                                            variant="h6"
                                            style={{marginTop: 24}}
                                        >
                                            Informations ({selectedRunInformations.length})
                                        </Typography>

                                        {selectedRunInformations.map(check => {
                                            const info =
                                                getInfoPresentation(check);

                                            return (
                                                <div
                                                    key={'info:' + (check.name || '')}
                                                    style={{
                                                        marginTop: 12,
                                                        padding: 16,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        style={{fontWeight: 700}}
                                                    >
                                                        INFORMATION — {info.title}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{marginTop: 8}}
                                                    >
                                                        {info.text}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            marginTop: 10,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        AUCUNE ACTION NÉCESSAIRE
                                                    </Typography>

                                                    <details style={{marginTop: 10}}>
                                                        <summary style={{cursor: 'pointer'}}>
                                                            Voir le détail technique
                                                        </summary>

                                                        <Typography
                                                            variant="body2"
                                                            style={{marginTop: 8}}
                                                        >
                                                            Contrôle : {check.name}
                                                        </Typography>

                                                        {check.detail && (
                                                            <Typography variant="body2">
                                                                Détail : {check.detail}
                                                            </Typography>
                                                        )}
                                                    </details>
                                                </div>
                                            );
                                        })}

                                </>
                            )}
                        
                                    </div>
                                )}
                                    </React.Fragment>
                                );
                            })}
                        </CardContent>
                    </Card>

                    <Card style={{marginTop: 20}}>
                        <CardContent>
                            <Typography
                                variant="h6"
                                style={{marginBottom: 12}}
                            >
                                Contrôles ({checks.length})
                            </Typography>

                            {checks.map(check => (
                                <div
                                    key={check.key || check.name}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 16,
                                        padding: '10px 0',
                                        borderBottom: '1px solid #ddd',
                                    }}
                                >
                                    <span>
                                        {tnrCheckLabel(check.name)}
                                    </span>
                                    <strong>
                                        {tnrStatusLabel(check.last_status)}
                                    </strong>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default TnrDashboard;
